import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import InfoTooltip from "@/components/UI/InfoTooltip";
import { PortfolioMetricTile } from "@/views/Portfolio/shared";
import {
  classify,
  docLabel,
  fetchTrialDocuments,
  groupSections,
  MetricsCard,
  SectionChecklistTable,
  type DocRow,
} from "@/components/Documents/tmf";
import { useSiteRisk } from "@/components/Risk/siteRisk";
import type { MasteredSite } from "../types";

/**
 * The site's own administrative file — the documents that belong to the site
 * rather than to any one protocol.
 *
 * An ISF mixes two kinds of paper: trial-specific records (protocol, IRB
 * approvals for this study, delegation log) and site-general records that
 * persist across studies and get re-filed into each active trial (staff CVs and
 * licences, GCP training certificates, lab accreditation, site contact and
 * qualification records). Trial-specific paper belongs on the trial; this tab is
 * the site-general half, which has no home on a trial screen.
 *
 * Membership is decided by ISF artifact section, not by a source flag — the feed
 * has no "site-general" marker — so the mapping below is ours and is stated in
 * the UI. Sections that Angela's ISF review places on the trial side
 * (delegation of authority, financial disclosure) are deliberately excluded even
 * though they concern site staff.
 */
const SITE_GENERAL_SECTIONS: Record<number, string> = {
  3: "Site contact details and qualifications",
  7: "Investigator qualification and site staff details",
  8: "Clinical research and study training",
  13: "Laboratory documentation",
  20: "Confidential site documents",
};

/** Credential classes whose currency matters across concurrent studies. */
const CREDENTIAL_SECTIONS = new Set([7, 8]);

/**
 * A document class earns a place on the *portfolio* site profile only if it
 * actually spans more than one of the site's trials. Anything confined to a
 * single trial is that trial's paper and belongs on the trial's own site
 * profile, however site-ish its artifact section looks.
 *
 * "Spans N trials" is measured by document label across the site's resolved
 * trials, and means exactly that — the class appears in more than one trial's
 * scope. It is NOT a claim that one physical document is shared: this feed
 * replicates the corpus per workspace, so each trial holds its own record.
 */
const MIN_TRIALS_TO_GENERALIZE = 2;

type CredentialRow = {
  key: string;
  label: string;
  trialsSpanned: number;
  records: number;
  distinctUploads: number;
  latestUpload: string | null;
  drift: boolean;
};

/** A document row plus the trial whose scope it was fetched under. */
type TaggedRow = { row: DocRow; trialId: string };

/** Group rows by document label, tracking which trials each label appears in. */
function groupByLabel(tagged: TaggedRow[]) {
  const byLabel = new Map<string, { rows: DocRow[]; trials: Set<string> }>();
  for (const { row, trialId } of tagged) {
    const label = docLabel(row);
    const entry = byLabel.get(label) ?? { rows: [], trials: new Set() };
    entry.rows.push(row);
    entry.trials.add(trialId);
    byLabel.set(label, entry);
  }
  return byLabel;
}

function buildCredentialRows(
  byLabel: ReturnType<typeof groupByLabel>,
): CredentialRow[] {
  const out: CredentialRow[] = [];
  for (const [label, { rows, trials }] of byLabel) {
    const docs = rows.filter(
      // An empty slot has no version to compare.
      (r) => r.file_name && CREDENTIAL_SECTIONS.has(classify(r).order),
    );
    if (docs.length === 0) continue;
    const uploads = new Set(
      docs.map((d) => (d.uploaded_date ?? "").slice(0, 10)).filter(Boolean),
    );
    out.push({
      key: label,
      label,
      trialsSpanned: trials.size,
      records: docs.length,
      distinctUploads: uploads.size,
      latestUpload: [...uploads].sort().pop() ?? null,
      // More than one filing date for the same credential means the site's
      // copies were collected at different times, so which one is current is a
      // real question rather than a certainty.
      drift: uploads.size > 1,
    });
  }
  return out.sort(
    (a, b) =>
      Number(b.drift) - Number(a.drift) || a.label.localeCompare(b.label),
  );
}

const CREDENTIAL_COLUMNS: ColumnsType<CredentialRow> = [
  {
    title: "Credential",
    dataIndex: "label",
    key: "label",
    ellipsis: true,
  },
  {
    title: () => (
      <span className="inline-flex items-center gap-1">
        Trials
        <InfoTooltip title="How many of this site's trials this credential appears in. The site's own paper recurs across its studies — that recurrence is why it belongs on the site rather than on one trial." />
      </span>
    ),
    dataIndex: "trialsSpanned",
    key: "trialsSpanned",
    width: 100,
  },
  { title: "Copies on file", dataIndex: "records", key: "records", width: 130 },
  {
    title: "Filing dates",
    dataIndex: "distinctUploads",
    key: "distinctUploads",
    width: 120,
  },
  {
    title: "Latest filing",
    dataIndex: "latestUpload",
    key: "latestUpload",
    width: 130,
    render: (date: string | null) =>
      date ?? <span className="text-[#B0BEC5]">—</span>,
  },
  {
    title: "Expires",
    key: "expires",
    width: 120,
    render: () => (
      <span className="inline-flex items-center gap-1 text-[#B0BEC5]">
        —
        <InfoTooltip title="Credential expiry needs document expiration dates at scale; the current extract carries only a handful, so renewal tracking cannot run yet." />
      </span>
    ),
  },
  {
    title: "",
    key: "drift",
    width: 150,
    render: (_: unknown, row: CredentialRow) =>
      row.drift ? (
        <span className="inline-flex items-center gap-1 rounded bg-[#FEF3C7] px-1.5 py-0.5 text-[11px] font-medium text-[#B45309]">
          check current version
          <InfoTooltip title="The same credential is on file with more than one filing date, so the copies in each binder may not be the same version." />
        </span>
      ) : null,
  },
];

export default function SiteDocumentsTab({ site }: { site: MasteredSite }) {
  // `site.trials` is derived by matching the golden id as a prefix of
  // trial_site_id, which is empty for an eISF feed whose trial_site_id is built
  // from the raw site id — so prefer the trial list the risk endpoint resolves
  // through the mastering graph, and fall back to the profile payload.
  const { data: risk, isLoading: riskLoading } = useSiteRisk(site.site_key);

  const trialIds = useMemo(() => {
    const resolved = risk?.trial_ids ?? [];
    const fromProfile = (site.trials ?? []).map((t) => t.trial_id);
    return [...new Set([...resolved, ...fromProfile])].filter(Boolean);
  }, [risk, site]);

  const queries = useQueries({
    queries: trialIds.map((trialId) => ({
      queryKey: ["documents", trialId],
      queryFn: () => fetchTrialDocuments(trialId),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const isLoading = riskLoading || queries.some((q) => q.isLoading);

  // Keep the trial each row was fetched under: which trials a document class
  // appears in is the test for whether it belongs on the site or on a trial.
  const tagged = useMemo(() => {
    const out: TaggedRow[] = [];
    queries.forEach((query, index) => {
      const trialId = trialIds[index];
      for (const row of query.data?.results ?? []) {
        if (!SITE_GENERAL_SECTIONS[classify(row).order]) continue;
        out.push({ row, trialId });
      }
    });
    return out;
  }, [queries, trialIds]);

  const byLabel = useMemo(() => groupByLabel(tagged), [tagged]);

  /** Only classes that recur across the site's trials belong at this level. */
  const generalizing = useMemo(() => {
    const kept = new Map<string, { rows: DocRow[]; trials: Set<string> }>();
    for (const [label, entry] of byLabel) {
      if (entry.trials.size >= MIN_TRIALS_TO_GENERALIZE) kept.set(label, entry);
    }
    return kept;
  }, [byLabel]);

  // Dedupe by document id for the checklist: the corpus is replicated per
  // workspace, so the same class arrives once per trial.
  const sections = useMemo(() => {
    const byId = new Map<string, DocRow>();
    for (const { rows } of generalizing.values()) {
      for (const row of rows) byId.set(row.document_id, row);
    }
    return groupSections([...byId.values()], Date.now());
  }, [generalizing]);

  const credentials = useMemo(
    () => buildCredentialRows(generalizing),
    [generalizing],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-[13px]">
        Loading…
      </div>
    );
  }

  if (trialIds.length < MIN_TRIALS_TO_GENERALIZE) {
    return (
      <div className="mx-auto flex max-w-[520px] flex-col items-center justify-center gap-2 py-16 text-center">
        <span className="text-[13px] font-medium text-[#48636F]">
          This site runs a single trial
        </span>
        <span className="text-[12px] text-[#8C8C8C]">
          Nothing can be shown to generalize across studies yet, so this
          site&apos;s documents live on that trial&apos;s site profile rather
          than here.
        </span>
      </div>
    );
  }

  if (generalizing.size === 0) {
    return (
      <div className="mx-auto flex max-w-[520px] flex-col items-center justify-center gap-2 py-16 text-center">
        <span className="text-[13px] font-medium text-[#48636F]">
          No documents recur across this site&apos;s trials
        </span>
        <span className="text-[12px] text-[#8C8C8C]">
          Every administrative document on file appears in only one of the{" "}
          {trialIds.length} trials, so each belongs on that trial&apos;s site
          profile.
        </span>
      </div>
    );
  }

  const expected = sections.reduce((n, s) => n + s.expected, 0);
  const collected = sections.reduce((n, s) => n + s.collected, 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-1.5 text-[12px] text-[#48636F]">
        Showing only documents that recur across this site&apos;s{" "}
        {trialIds.length} trials
        <InfoTooltip title="This level holds the site's own paper — what persists across its studies. A document confined to one trial is that trial's record and appears on the trial's site profile instead. Recurrence is measured by document class across the site's trials; each trial still holds its own copy." />
      </div>

      <MetricsCard columns={4}>
        <PortfolioMetricTile
          label="Artifact sections"
          value={sections.length}
        />
        <PortfolioMetricTile label="Documents on file" value={collected} />
        <PortfolioMetricTile label="Expected slots" value={expected} />
        <PortfolioMetricTile
          label="Credentials tracked"
          value={credentials.length}
        />
      </MetricsCard>

      <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-[13px] font-semibold text-[#1F2933]">
            Credential currency
          </span>
          <InfoTooltip title="Staff CVs, licences and training certificates held by this site. Where the same credential is on file with several filing dates, the copies in each binder may differ — the question a monitor asks when a site runs several studies at once." />
        </div>
        {credentials.length === 0 ? (
          <div className="text-[13px] text-[#8C8C8C]">
            No collected credential documents — only empty template slots.
          </div>
        ) : (
          <Table<CredentialRow>
            columns={CREDENTIAL_COLUMNS}
            dataSource={credentials}
            rowKey="key"
            size="small"
            pagination={false}
            scroll={{ x: 900 }}
          />
        )}
      </div>

      <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-[13px] font-semibold text-[#1F2933]">
            Site-general sections
          </span>
          <InfoTooltip
            title={`Documents that belong to the site rather than to one protocol, by ISF artifact section: ${Object.values(
              SITE_GENERAL_SECTIONS,
            ).join(
              "; ",
            )}. Trial-specific paper — including delegation of authority and financial disclosure — stays on the trial.`}
          />
        </div>
        <SectionChecklistTable sections={sections} />
      </div>
    </div>
  );
}
